import { ref, watch } from 'vue';
import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { ExportTicket, ExportTicketStatus } from '@/types/exportTicket';
import { useUserStore } from '@/store/userStore';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { CardBulkOrder, CardBulkOrderStatus } from '@/components/cards/types/cardBulkOrder';
import { NaturalPersonURI } from '@/types/naturalPerson';

export const useExportsStore = defineStore('exports', () => {
  const userStore = useUserStore();
  const eventStreamStore = useEventStreamStore();
  // STATES
  const dataSharingExports = ref<ExportTicket[]>([]);
  const cardBulkOrderExports = ref<CardBulkOrder[]>([]);

  // GETTERS
  function getDataSharingExports() {
    return dataSharingExports.value;
  }
  function getCardBulkOrderExports() {
    return cardBulkOrderExports.value;
  }

  // ACTIONS
  function setDataSharingExports(et: ExportTicket[]) {
    dataSharingExports.value = et;
  }
  function setCardBulkOrderExports(et: CardBulkOrder[]) {
    cardBulkOrderExports.value = et;
  }

  async function loadDataSharingExports() {
    const rsp = await apiGw.get(`${NaturalPersonURI.Base}/${userStore.uid}${NaturalPersonURI.ExportTickets}`);
    if (rsp) {
      setDataSharingExports(
        rsp.data
          .filter(
            (t: ExportTicket) =>
              t.status === ExportTicketStatus.New ||
              t.status === ExportTicketStatus.Active ||
              t.status === ExportTicketStatus.Used
          )
          .sort((a: ExportTicket, b: ExportTicket) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      );
    }
  }

  async function loadCardBulkOrderExports(cardProgramUid: string) {
    const rsp = await apiGw.get(`/v2/card-programs/${cardProgramUid}/extend-bulk`);
    setCardBulkOrderExports(
      rsp.data.filter(
        (t: CardBulkOrder) =>
          t.status === CardBulkOrderStatus.New ||
          t.status === CardBulkOrderStatus.Executed ||
          t.status === CardBulkOrderStatus.Failed
      )
    );
  }

  function $reset() {
    dataSharingExports.value = [];
    cardBulkOrderExports.value = [];
  }

  watch(
    () => eventStreamStore.stale_export_ticket,
    async (newVal) => {
      if (!newVal) return;
      if (dataSharingExports.value.length === 0) await userStore.refreshUser();
      loadDataSharingExports();
    },
    { deep: true }
  );

  return {
    dataSharingExports,
    getDataSharingExports,
    setDataSharingExports,
    loadDataSharingExports,
    cardBulkOrderExports,
    getCardBulkOrderExports,
    setCardBulkOrderExports,
    loadCardBulkOrderExports,
    $reset
  };
});
